/**
 * Analytics Dashboard Page
 * Comprehensive business intelligence dashboard
 */

import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { MetricCard } from '../../components/analytics/MetricCard';
import { RevenueChart } from '../../components/analytics/RevenueChart';
import { ClientGrowthChart } from '../../components/analytics/ClientGrowthChart';
import { ProductPerformanceTable } from '../../components/analytics/ProductPerformanceTable';
import { ServiceDistributionChart } from '../../components/analytics/ServiceDistributionChart';

interface DashboardData {
  revenue: any;
  clients: any;
  services: any;
  topProducts: any[];
}

export const AnalyticsDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [revenueTimeSeries, setRevenueTimeSeries] = useState<any[]>([]);
  const [clientTimeSeries, setClientTimeSeries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Date range state
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    compare: true,
  });

  useEffect(() => {
    loadDashboardData();
  }, [dateRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate).toISOString(),
        compare: dateRange.compare.toString(),
      });

      // Load all dashboard data in parallel
      const [dashboardResponse, revenueSeriesResponse, clientSeriesResponse] = await Promise.all([
        adminApi.get(`/analytics/dashboard?${params.toString()}`),
        adminApi.get(`/analytics/revenue/timeseries?${params.toString()}&granularity=day`),
        adminApi.get(`/analytics/clients/timeseries?${params.toString()}&granularity=day`),
      ]);

      setData(dashboardResponse.data.metrics);
      setRevenueTimeSeries(revenueSeriesResponse.data.timeSeries);
      setClientTimeSeries(clientSeriesResponse.data.timeSeries);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard data');
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field: string, value: any) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const setPresetRange = (preset: string) => {
    const endDate = new Date().toISOString().split('T')[0];
    let startDate: string;

    switch (preset) {
      case '7days':
        startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case '30days':
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case '90days':
        startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case '1year':
        startDate = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      default:
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    }

    setDateRange((prev) => ({ ...prev, startDate, endDate }));
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white">Analytics Dashboard</h1>
          <p className="text-slate-400 mt-1">Business intelligence and performance metrics</p>
        </div>

        {/* Date Range Controls */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6 border border-slate-700/50">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-slate-300">From:</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                className="px-3 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded text-white text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm text-slate-300">To:</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                className="px-3 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded text-white text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div className="h-6 w-px bg-slate-700/50"></div>

            <div className="flex space-x-2">
              <button
                onClick={() => setPresetRange('7days')}
                className="px-3 py-1.5 text-sm bg-slate-700/50 hover:bg-slate-600/50 text-white rounded transition-colors"
              >
                Last 7 Days
              </button>
              <button
                onClick={() => setPresetRange('30days')}
                className="px-3 py-1.5 text-sm bg-slate-700/50 hover:bg-slate-600/50 text-white rounded transition-colors"
              >
                Last 30 Days
              </button>
              <button
                onClick={() => setPresetRange('90days')}
                className="px-3 py-1.5 text-sm bg-slate-700/50 hover:bg-slate-600/50 text-white rounded transition-colors"
              >
                Last 90 Days
              </button>
              <button
                onClick={() => setPresetRange('1year')}
                className="px-3 py-1.5 text-sm bg-slate-700/50 hover:bg-slate-600/50 text-white rounded transition-colors"
              >
                Last Year
              </button>
            </div>

            <div className="h-6 w-px bg-slate-700/50"></div>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={dateRange.compare}
                onChange={(e) => handleDateRangeChange('compare', e.target.checked)}
                className="w-4 h-4 bg-slate-700/50 border-slate-600/50 rounded text-orange-600"
              />
              <span className="text-sm text-slate-300">Compare with previous period</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <MetricCard
                title="Total Revenue"
                value={`$${data.revenue.totalRevenue.toFixed(2)}`}
                icon="currency"
                color="blue"
                change={data.revenue.comparisonData?.percentageChange}
                changeLabel={
                  data.revenue.comparisonData
                    ? `vs previous period ($${data.revenue.comparisonData.previousRevenue.toFixed(2)})`
                    : undefined
                }
              />

              <MetricCard
                title="New Clients"
                value={data.clients.newClients.toString()}
                icon="users"
                color="green"
                change={data.clients.comparisonData?.percentageChange}
                changeLabel={
                  data.clients.comparisonData
                    ? `vs previous period (${data.clients.comparisonData.previousClients})`
                    : undefined
                }
              />

              <MetricCard
                title="Active Services"
                value={data.services.activeServices.toString()}
                icon="server"
                color="purple"
                subtitle={`${data.services.activationRate}% activation rate`}
              />

              <MetricCard
                title="Churn Rate"
                value={`${data.clients.churnRate}%`}
                icon="trending"
                color={data.clients.churnRate < 5 ? 'green' : data.clients.churnRate < 10 ? 'yellow' : 'red'}
                subtitle={`${data.clients.retentionRate}% retention`}
              />
            </div>

            {/* Secondary Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <MetricCard
                title="Net Revenue"
                value={`$${data.revenue.netRevenue.toFixed(2)}`}
                icon="chart"
                color="indigo"
                subtitle={`$${data.revenue.refundedAmount.toFixed(2)} refunded`}
              />

              <MetricCard
                title="Avg Order Value"
                value={`$${data.revenue.averageOrderValue.toFixed(2)}`}
                icon="shopping"
                color="pink"
                subtitle={`${data.revenue.paidInvoices} paid invoices`}
              />

              <MetricCard
                title="Avg Revenue Per User"
                value={`$${data.clients.averageRevenuePerUser.toFixed(2)}`}
                icon="user-dollar"
                color="orange"
                subtitle={`${data.clients.activeClients} active clients`}
              />

              <MetricCard
                title="Pending Revenue"
                value={`$${data.revenue.pendingRevenue.toFixed(2)}`}
                icon="clock"
                color="yellow"
                subtitle="Unpaid invoices"
              />
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <RevenueChart data={revenueTimeSeries} loading={loading} />
              <ClientGrowthChart data={clientTimeSeries} loading={loading} />
            </div>

            {/* Service Distribution and Top Products */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <ServiceDistributionChart data={data.services} loading={loading} />
              <ProductPerformanceTable products={data.topProducts} loading={loading} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
